import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, BookOpen, User, Search, Moon, Sun, Settings } from 'lucide-react';

import { LiquidGlass } from '@/components/ui/LiquidGlass';
import { LiquidBar } from '@/components/ui/LiquidBar';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/stores/authStore';

export const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true' ||
      (!localStorage.getItem('darkMode') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setIsDarkMode(isDark);
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.documentElement.classList.toggle('dark', newDarkMode);
  };

  const navigation = [
    { name: '홈', href: '/', icon: Home },
    { name: '블로그', href: '/blog', icon: BookOpen },
    { name: '소개', href: '/about', icon: User },
    { name: '검색', href: '/search', icon: Search },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      {/* 햄버거 메뉴 버튼 - 오른쪽 상단 고정 */}
      <div className="fixed top-6 right-6 z-50 flex items-center space-x-3">
        {/* 다크 모드 토글 */}
        <motion.button
          onClick={toggleDarkMode}
          className="p-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-colors shadow-lg"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.9 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={isDarkMode ? 'dark' : 'light'}
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.2 }}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-blue-400" />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.button>

        {/* 메뉴 버튼 */}
        <motion.button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-colors shadow-lg"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.9 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={isMenuOpen ? 'close' : 'menu'}
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.2 }}
            >
              {isMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.button>
      </div>

      {/* 네비게이션 메뉴 - 메뉴 버튼 클릭시 중앙에 나타남 */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* 배경 오버레이 */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* 메뉴 컨텐츠 - 화면 중앙 */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <LiquidBar
                position="center"
                variant="vibrant"
              >
                <div className="flex items-center space-x-2 px-4">
                  {/* 로고 */}
                  <Link
                    to="/"
                    className="flex items-center space-x-2 group mr-6"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <motion.div
                      className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-sm"
                      whileHover={{ scale: 1.02, rotate: 2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      H
                    </motion.div>
                    <span className="font-bold text-sm text-foreground group-hover:text-blue-400 transition-colors">
                      HM.dev
                    </span>
                  </Link>

                  {/* 네비게이션 메뉴 */}
                  {navigation.map(({ name, href, icon: Icon }) => (
                    <Link
                      key={href}
                      to={href}
                      onClick={() => setIsMenuOpen(false)}
                      className={cn(
                        'relative px-3 py-2 rounded-full transition-all duration-300 flex items-center space-x-2 group',
                        isActive(href)
                          ? 'text-blue-400'
                          : 'text-foreground hover:text-blue-400'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="font-medium text-sm">{name}</span>
                      {isActive(href) && (
                        <motion.div
                          className="absolute inset-0 bg-blue-500/20 rounded-full"
                          layoutId="activeTab"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                    </Link>
                  ))}

                  {/* 관리자 메뉴 */}
                  {isAuthenticated && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className={cn(
                        'relative px-3 py-2 rounded-full transition-all duration-300 flex items-center space-x-2 group',
                        isActive('/admin')
                          ? 'text-cyan-400'
                          : 'text-foreground hover:text-cyan-400'
                      )}
                    >
                      <Settings className="w-4 h-4" />
                      <span className="font-medium text-sm">관리자</span>
                      {isActive('/admin') && (
                        <motion.div
                          className="absolute inset-0 bg-cyan-500/20 rounded-full"
                          layoutId="activeTab"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                    </Link>
                  )}
                </div>
              </LiquidBar>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
};